<template>
  <div class="px-5 py-5 lg:px-10 xl:px-10">
  <notifications position="top center"></notifications>

  <div class="flex flex-col center-content mb-10">
    <h2 class="font-bold text-2xl mb-2">Edit your fundraising page</h2>
    <p class="text-sm">Did you know that a personalized fundraising page can significantly increase your chances of hitting your goal? By editing your page, you can tell your unique story, share why this cause matters to you, and connect on a deeper level with your potential supporters. Customize your message, add photos or videos, and make your appeal stand out. Remember, the more personal and compelling your page, the more likely people are to donate.</p>
  </div>

  <div class="space-y-10">

    <div v-if="showStandard == false" class="flex -mx-2"> <!-- Added flex utilities and some negative margin for gutters -->
      <div class="w-1/3 px-2">
        <label for="campaignName" class="block text-base font-bold text-black">
          Campaign Name <sup class="text-red required">*</sup>
        </label>
        <div class="mt-1">
          <input type="text" v-model="form.name" id="campaignName" autocomplete="" class="w-full block sm:text-sm rounded-md border-gray-300 focus:outline-none focus:ring-pink-500 focus:border-pink-500">
        </div>
      </div>  

      <div class="w-1/3 px-2">
        <label :for="campaignLabel.toLowerCase().replace(' ', '')" class="block text-base font-bold text-black">
          {{ campaignLabel }} <sup class="text-red required">*</sup>
        </label>
        <div class="mt-1">
          <input type="text" v-model="form.business_name" :id="campaignLabel.toLowerCase().replace(' ', '')" autocomplete="" class="w-full block sm:text-sm rounded-md border-gray-300 focus:outline-none focus:ring-pink-500 focus:border-pink-500">
        </div>
      </div>
    </div>

    <div class="w-full mt-4">
      <label for="campaignGoal" class="block text-base font-bold text-black mb-2">
        Fundraising Goal <sup class="text-red required">*</sup>
      </label>
      <p class="text-sm mb-4">Setting a fundraising goal isn't just about having a number—it's about showing your supporters the impact you’re aiming to make. By setting a clear goal, you help your friends and family understand the difference you’re striving for.</p>
      <div class="flex flex-wrap items-end gap-4">
        <div v-for="amount in [1500, 2500, 5000, 10000]" :key="amount" class="flex flex-col items-center flex-grow">
          <button 
            @click="selectGoal(amount)"
            :class="[
              'px-4 py-3 rounded-md border-2 border-pink-500 font-bold w-full',
              form.goal == amount ? 'bg-pink-500 text-white' : 'bg-pink-100 text-black hover:bg-pink-200'
            ]"
          >
            ${{ amount.toLocaleString() }}
          </button>
          <span v-if="amount === 2500" class="block text-xs mt-1 text-black font-semibold">RECOMMENDED</span>
          <div v-else class="block text-xs mt-1 text-gray-600 font-medium">&nbsp;</div> <!-- Spacer for non-recommended buttons -->
        </div>
        <div class="flex flex-col items-center flex-grow">
          <div class="flex items-center w-full">
            <label for="customGoal" class="block text-sm font-bold text-black mr-2">Other:</label>
            <div class="relative flex items-center flex-grow">
              <span class="absolute left-3 text-gray-500">$</span>
              <input 
                type="text" 
                v-model="customGoal" 
                @input="updateGoal"
                id="customGoal" 
                class="w-full pl-7 pr-2 py-3 rounded-md border-gray-300 focus:outline-none focus:ring-pink-500 focus:border-pink-500"
              >
            </div>
          </div>
          <div class="block text-xs mt-1 text-gray-600 font-medium">&nbsp;</div>
        </div>
      </div>
    </div>

    <div v-if="isPledgeDrive && isFoundation">
    <div>
      <label  class="block text-base font-bold text-black">Pledge Drive End Date<sup class="text-red required">*</sup></label>
      <div class="mt-1">
      <flat-pickr v-model="form.pledge_drive_end_date" class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm rounded-md border-gray-300"></flat-pickr>
      </div>
    </div>
    </div>

    <div v-if="isPledgeDrive && isFoundation">
    <label for="campaignGoal" class="block text-base font-bold text-black">
      Pledge Drive Activity Type <sup class="text-red required">*</sup>
    </label>
    <div class="mt-1">
      <input type="text" v-model="form.pledge_drive_activity_type" class="w-full block sm:text-sm rounded-md border-gray-300">
    </div>
    </div>

    <div v-if="form.view_type === 'Refreshed'">
      <label class="block text-base font-bold text-black mb-2">
        Your Media Gallery
      </label>
      <p class="text-sm mb-4">Enhance your fundraising page with a media gallery! Upload images by clicking the upload button, and add videos by uploading them to YouTube and pasting the link into the video field. A vibrant media gallery makes your page more engaging and personal, helping to boost donations.</p>
      <div class="mb-6">
        <label for="youtubeUrl" class="block text-sm font-bold text-black">Add YouTube Video</label>
        <div class="mt-1 flex items-center space-x-2">
          <input type="text" name="youtubeUrl" id="youtubeUrl" v-model="youtubeUrl"
                 class="flex-1 px-3 py-3 rounded-md focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm border-gray-300"
                 placeholder="https://www.youtube.com/watch?v=...">
          <a @click="addYoutubeVideo" class="cursor-pointer bg-pink hover:bg-pink-600 border-2 border-transparent text-white font-medium uppercase text-center px-6 py-3 text-sm rounded-full transform transition-all duration-500 inline-flex items-center">
            <i class="fas fa-video mr-2"></i> Add Video
          </a>
        </div>
      </div>
      <div class="flex flex-col md:flex-row items-start">
        <div class="w-full md:w-auto md:mr-4 mb-4 md:mb-0">
          <div class="uploader">
            <Uploader
              :server="`/campaign/update-campaign-slider?campaign_id=${this.campaign.id}`"
              :media="sliderGalleryMediaComputed"
              :location="sliderGalleryLocation"
              :max="10"
              path="/storage/media"
              :headers="{
                'X-CSRF-TOKEN': this.token,
              }"
              @add="addMediaHandler"
              @remove="removeMediaHandler"
              ref="uploader"
            />
          </div>
        </div>
        <div class="w-full">
          <draggable 
            v-model="sliderGalleryMediaComputed" 
            @end="onDragEnd"
            item-key="name"
            class="grid grid-cols-2 gap-5 sm:flex sm:flex-row sm:flex-wrap sm:gap-4 mb-4"
          >
            <template #item="{ element, index }">
              <div class="media-item relative">
                <img :src="`${sliderGalleryLocation}/${element.name}`" :alt="element.name" class="thumbnail rounded-lg w-full h-auto" />
                <div class="tooltip absolute bg-gray-800 text-white px-2 py-1 rounded text-sm invisible opacity-0 transition-all duration-300" :class="{ 'visible opacity-100': isTooltipVisible(element.name) }">
                  {{ element.name }}
                </div>
                <button class="delete-btn absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center" @click="deleteMedia(index)">
                  <i class="fas fa-trash-alt"></i>
                </button>
                <div class="media-type-icon absolute top-2 left-2 bg-gray-800 bg-opacity-50 text-white rounded-full w-6 h-6 flex items-center justify-center">
                  <i :class="element.type === 'video' || element.type === 'youtube' ? 'fas fa-video' : 'fas fa-image'"></i>
                </div>
              </div>
            </template>
          </draggable>
        </div>
      </div>
      <p class="text-sm text-gray-600">Maximum file size: 4MB. Landscape photos will look best.</p>
    </div>

    <div>
    <label class="block text-base font-bold text-black mb-2">
      About your Campaign
    </label>
    <p class="text-sm mb-4">Share your story and connect with your supporters in the "About Your Campaign" section. Use the editor below to add text, images, and videos that highlight why this cause matters to you. A compelling story can inspire more donations and help your campaign stand out. Let your passion shine through and show your supporters the impact they're contributing to.</p>

    <div class="mt-1">
      <quill-editor
        ref="myQuillEditor"
        :modules="modules"
        v-model:content="form.message"
        contentType="html"
        toolbar="full"
        class="rounded-b-md border-gray-300"
      />
    </div>
    </div>

    <div>
      <label class="block text-base font-bold text-black mb-2">
        Event Listing
      </label>
      <p class="text-sm mb-4">Whether you have a single main event or multiple smaller events like bake sales or car washes, you can list them all here. Just click the ADD EVENT button and enter each event one at a time.</p>
      <div class="flex justify-center sm:justify-start mb-7">
        <add-campaign-event-button
          ref="addCampaignEventButton"
          :campaign-name="campaign.name"
          :campaign-id="campaign.id"
          @event-added="fetchCampaignEvents"
        ></add-campaign-event-button>
      </div>

      <div class="mt-6">
        <!-- Events table (small breakpoint and up) -->
        <div class="hidden sm:block">
          <div class="align-middle inline-block min-w-full border-b border-gray-200">
            <table class="min-w-full">
              <thead>
                <tr class="border-t border-gray-200">
                  <th class="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th class="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Start Date
                  </th>
                  <th class="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    End Date
                  </th>
                  <th class="px-6 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th class="pr-6 py-3 border-b border-gray-200 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-100">
                <tr v-for="event in campaignEvents" :key="event.id" class="hover:bg-gray-100">
                  <td class="px-6 py-3 text-sm text-gray-500 text-left">
                    {{ event.title }}
                  </td>
                  <td class="px-6 py-3 text-sm text-gray-500 text-left">
                    {{ event.begins_at ? formatDate(event.begins_at) : '&nbsp;' }}
                  </td>
                  <td class="px-6 py-3 text-sm text-gray-500 text-left">
                    {{ event.end_date ? formatDate(event.end_date) : '&nbsp;' }}
                  </td>
                  <td class="px-6 py-3 text-sm text-gray-500 text-left">
                    {{ trimLocation(event.fullAddress) }}
                  </td>
                  <td class="pr-6">
                    <div class="relative flex justify-end items-center space-x-2">
                      <button @click="editEvent(event)" class="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900" role="menuitem">
                        <svg class="mr-3 h-5 w-5 text-blue-500 group-hover:text-blue-700" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                      </button>
                      <button @click="deleteEvent(event.id)" class="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900" role="menuitem">
                        <svg class="mr-3 h-5 w-5 text-pink-500 group-hover:text-pink-700" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Mobile view for events -->
        <div class="sm:hidden">
          <ul class="divide-y divide-gray-200">
            <li v-for="event in campaignEvents" :key="event.id" class="py-2">
              <div class="flex justify-between items-center">
                <div class="flex-1 min-w-0 pr-2">
                  <p class="text-sm font-medium text-gray-900 truncate">
                    {{ event.title }}
                  </p>
                  <p class="text-xs text-gray-500">
                    {{ event.begins_at ? formatDate(event.begins_at) : 'No start date' }}
                  </p>
                  <p class="text-xs text-gray-500 truncate">
                    {{ trimLocation(event.fullAddress) }}
                  </p>
                </div>
                <div class="flex space-x-2">
                  <button @click="editEvent(event)" class="text-blue-500">
                    <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                    </svg>
                  </button>
                  <button @click="deleteEvent(event.id)" class="text-pink-500">
                    <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div v-if="form.view_type !== 'Refreshed'">
      <label class="block text-base font-bold text-black">
        Logo Image
      </label>
      <p class="mb-3 text-sm">Let people know who you are by adding a picture of yourself or a logo from your event or your organization.</p>

      <div class="border-2 border-gray-300 border-dashed ">
        <DropZone
          ref="dropzone"
          :clickable="true"
          url="/campaign/update-campaign-logo"
          :maxFiles="1"
          :uploadOnDrop="false"
          :multipleUpload="false"
          :headers="{'x-csrf-token': this.token}"
          @sending="sending"
          dropzone-message-class-name="dropzone__message--dropzone1"
        >
        <template v-slot:message><p>To upload your logo/icon, drop a JPG, GIF or PNG file you would like to use here.</p></template>
        </DropZone>
      </div>
    </div>

    <div v-if="form.view_type !== 'Refreshed'">
    <label class="block text-base font-bold text-black">
      Current Logo
    </label>
    <div class="mt-1">
      <img class="w-14"  :src="campaign.thumbnailPath" alt="">
    </div>
    </div>

    <div v-if="form.view_type != 'Refreshed'">
      <div>
      <label for="hero_image_selection" class="block text-base font-bold text-black">
        Hero Image Background
      </label>
      <p class="mb-3 text-sm">The hero image is the big image that sits behind your logo. You can use one of our colorful her backgrounds OR upload your own.</p>
      <div class="mt-1">
        <select v-model="uploadYourHero" id="hero_image_selection" class="w-full block sm:text-sm">
        <option value="ours">Use one of our backgrounds</option>
        <option value="yours">Upload your own</option>
        </select>
      </div>
      </div>

      <div  v-show="uploadYourHero === `yours`" class="mb-3">
      <label class="block text-base font-bold text-black">
        Upload your Image
      </label>
      <div class="border-2 border-gray-300 border-dashed ">

        <DropZone
          ref="dropzone2"
          :clickable="true"
          url="/campaign/update-campaign-hero-logo"
          :maxFiles="1"
          :uploadOnDrop="false"
          :multipleUpload="false"
          :headers="{'x-csrf-token': this.token}"
          @sending="sending"
          dropzone-message-class-name="dropzone__message--dropzone2"
        >
        <template v-slot:message><p>To upload your logo/icon, drop a JPG, GIF or PNG file you would like to use here.</p></template>
        </DropZone>
      </div>
      </div>

      <div v-show="uploadYourHero === `ours`">
      <p class="mb-3 text-sm">Select one of the images below:</p>
      <div class="my-2 px-2 overflow-hidden flex inline space-x-5">
        <div class="flex items-center w-1/4">
        <input v-model="form.hero_image_text" value="pink" name="hero_image_text" type="radio" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300">
        <label class="ml-3 block text-base font-bold text-black">
          <img class="w-full" src="/img/side-out-gradient-bg-pink@2x.jpg" alt="">
        </label>
        </div>
        <div class="flex items-center w-1/4">
        <input v-model="form.hero_image_text" value="blue" name="hero_image_text" type="radio" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300">
        <label  class="ml-3 block text-base font-bold text-black">
          <img class="w-full" src="/img/side-out-gradient-bg-blue@2x.jpg" alt="">
        </label>
        </div>
      </div>
      <div class="my-2 px-2 overflow-hidden flex inline space-x-5">
        <div class="flex items-center w-1/4">
        <input v-model="form.hero_image_text" value="green" name="hero_image_text" type="radio" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300">
        <label class="ml-3 block text-base font-bold text-black">
          <img class="w-full" src="/img/side-out-gradient-bg-green@2x.jpg" alt="">
        </label>
        </div>
        <div class="flex items-center w-1/4">
        <input v-model="form.hero_image_text"  value="purple-blue" name="hero_image_text" type="radio" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300">
        <label  class="ml-3 block text-base font-bold text-black">
          <img class="w-full" src="/img/side-out-gradient-bg@2x.jpg"  alt="">
        </label>
        </div>
      </div>
      </div>


      <div>
      <label class="block text-base font-bold text-black">
        Current Hero Image Background
      </label>
      <div class="mt-1">
        <img class="w-1/4" :src="campaign.hero_image_path" alt="">
      </div>
      </div>
    </div>  

    <div v-if="form.view_type !== 'Refreshed'" class="mt-12 space-y-4 sm:mt-16 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-6 lg:max-w-4xl lg:mx-auto xl:max-w-none xl:mx-0 xl:grid-cols-4">

      <div v-if="isFoundation === true && form.view_type !== 'Refreshed'" class="border border-gray-200 rounded-lg shadow-sm divide-y divide-gray-200">
        <label  class="pl-5 block text-base font-bold text-black">All Cards</label>
        <draggable class="list-group" :list="cards" group="people" v-bind="dragOptions" item-key="id">
        <template #item="{element}">
          <div class="pt-1 pb-1 px-2" :key="element.title">
          <p class="mt-1 text-sm text-gray-500"> {{ element.title }} - <i>{{ element.type }}</i> <span class="font-italic" style="font-size: 10px">({{ getImpressionCount(element) }})</span></p>
          </div>
        </template>
        </draggable>
      </div>


      <div v-if="form.view_type !== 'Refreshed'" class="border border-gray-200 rounded-lg shadow-sm divide-y divide-gray-200">
        <label  class="pl-5  block text-base font-bold text-black">Selected Cards</label>
        <draggable class="list-group" :list="form.campaignCards" group="people" v-bind="dragOptions" item-key="ids">
        <template #item="{element}">
          <div  class="pt-1 pb-1 px-2"  :key="element.title"   >
          <p class="mt-1 text-sm text-gray-500"> {{ element.title }} - <i>{{ element.type }}</i> <span class="font-italic"  style="font-size: 10px">({{ getImpressionCount(element) }})</span></p>
          </div>
        </template>
        </draggable>
      </div>

    </div>

    <div class="flex flex-row justify-center sm:justify-start space-x-3">
    <!-- Save Button -->
    <button @click="updateCampaign" type="button" class="flex-1 sm:flex-none inline-flex items-center justify-center text-center text-sm uppercase px-6 py-3 border-2 border-transparent font-medium shadow-sm text-white bg-green-500 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-200 rounded-full">
      <i class="fas fa-check mr-2"></i> Save
    </button>

    <!-- Cancel Button (as a link) -->
    <a href="/" class="flex-1 sm:flex-none inline-flex items-center justify-center text-center text-sm uppercase px-6 py-3 border-2 border-transparent font-medium shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-200 rounded-full">
      <i class="fas fa-ban mr-2"></i> Cancel
    </a>
    </div>


  </div>

  </div>
</template>

<script>
import { defineComponent } from 'vue';
import DropZone from 'dropzone-vue';
import 'dropzone-vue/dist/dropzone-vue.common.css';
import Uploader from 'vue-media-upload';
import draggable from 'vuedraggable';
import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import { QuillEditor } from '@vueup/vue-quill';
import BlotFormatter from 'quill-blot-formatter';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import AddCampaignEventButton from './campaign-event/AddCampaignEventButton.vue';
import EditCampaignEventLink from './campaign-event/EditCampaignEventLink.vue';

export default defineComponent({
  name: 'EditCampaign',
  components: {
    draggable,
    flatPickr,
    QuillEditor,
    DropZone,
    Uploader,
    AddCampaignEventButton,
    EditCampaignEventLink
  },
  props: {
    myCampaign: {
      type: Object,
      required: false,
    },
  },
  setup() {
    const modules = {
      name: 'blotFormatter',  
      module: BlotFormatter, 
      options: {/* options */}
    }
    return { modules }
  },
  data() {
    return {
      tooltip: {
        campaign: false,
        school: false,
        eventDate: false,
        campaignGoal: false,
        message: false,
        media: false
      },
      sliderGalleryLocation: '',
      sliderGalleryMedia: [],
      myFile: null,
      campaign: this.myCampaign || {},
      parentCampaign: null,
      campaignEvents: [],
      editingEvent: null,
      form: {
        view_type: this.myCampaign.view_type,
        name: this.myCampaign.name,
        business_name: this.myCampaign.business_name,
        goal: this.myCampaign.goal,
        hero_image_text: this.myCampaign.hero_image_text,
        pledge_drive_end_date: this.myCampaign.pledge_drive_end_date,
        pledge_drive_activity_type: this.myCampaign.pledge_drive_activity_type,
        message: this.myCampaign.message,
        ownership: this.myCampaign.ownership,
        event_date: this.myCampaign.formattedEventDate,
        campaignCards: [],
        impact_slider_slide_one_title: this.myCampaign.impact_slider_slide_one_title ?? '',
        impact_slider_slide_two_title: this.myCampaign.impact_slider_slide_two_title ?? '',
        impact_slider_slide_three_title: this.myCampaign.impact_slider_slide_three_title ?? '',
        impact_slider_slide_one_subtitle: this.myCampaign.impact_slider_slide_one_subtitle ?? '',
        impact_slider_slide_two_subtitle: this.myCampaign.impact_slider_slide_two_subtitle ?? '',
        impact_slider_slide_three_subtitle: this.myCampaign.impact_slider_slide_three_subtitle ?? '',
        impact_slider_slide_one_text: this.myCampaign.impact_slider_slide_one_text ?? '',
        impact_slider_slide_two_text: this.myCampaign.impact_slider_slide_two_text ?? '',
        impact_slider_slide_three_text: this.myCampaign.impact_slider_slide_three_text ?? '',
      },
      cards: [],
      showStandard: true,
      uploadYourHero: 'ours',
      saving: 'Save',
      hoveredThumbnail: null,
      youtubeUrl: '',
      customGoal: '',
    }
  },
  created() {
    this.getCampaign().then(() => {
      if (this.campaign && this.campaign.id) {
        this.fetchCampaignEvents();
      } else {
        console.error('Campaign not properly initialized');
        this.$notify({ text: 'Unable to load campaign data', type: 'error' });
      }
    });
  },
  async mounted() {
    if (this.campaign.parent_id) {
      await this.getParentCampaign()
    }
    this.initializeSliderGallery()
    this.initializeGoal()
  },
  methods: {
    trimLocation(fullAddress) {
      if (!fullAddress) return '';
      const parts = fullAddress.split(',').map(part => part.trim()).filter(Boolean);
      return parts.join(', ');
    },
    initializeGoal() {
      const predefinedGoals = [1500, 2500, 5000, 10000];
      if (!predefinedGoals.includes(parseFloat(this.form.goal))) {
        this.customGoal = this.form.goal;
      }
    },
    fetchCampaignEvents() {
      axios.get(`/campaign/${this.campaign.id}/events`)
        .then(response => {
          console.log('Fetched campaign events:', response.data);
          this.campaignEvents = response.data;
        })
        .catch(error => {
          console.error('Error fetching campaign events:', error);
          console.log('Error response:', error.response);
          this.$notify({ 
            text: `Failed to fetch campaign events: ${error.response?.data?.message || error.message}`, 
            type: 'error' 
          });
        });
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    deleteEvent(eventId) {
      if (confirm('Are you sure you want to delete this event?')) {
        axios.delete(`/campaign/${this.campaign.id}/events/${eventId}`)
          .then(response => {
            this.campaignEvents = this.campaignEvents.filter(event => event.id !== eventId);
            this.$notify({ text: 'Event deleted successfully', type: 'success' });
          })
          .catch(error => {
            console.error('Error deleting event:', error);
            this.$notify({ text: 'Failed to delete event', type: 'error' });
          });
      }
    },
    editEvent(event) {
      this.editingEvent = event;
      this.$refs.addCampaignEventButton.openModal(event);
    },
  async getParentCampaign() {
    try {
      const response = await axios.get(`/campaign/${this.campaign.parent_id}`)
      this.parentCampaign = response.data.campaign
    } catch (error) {
      console.error('Error fetching parent campaign:', error)
    }
  },
  selectGoal(amount) {
    this.form.goal = amount;
    this.customGoal = '';
  },
  updateGoal() {
    if (this.customGoal) {
      this.form.goal = parseFloat(this.customGoal);
    }
  },
  onDragEnd() {
    this.updateMediaOrder();
  },
  updateMediaOrder() {
    const formData = new FormData();
    formData.append('campaign_id', this.campaign.id);
    
    // Append the current order of files
    this.sliderGalleryMediaComputed.forEach((item, index) => {
      const fullPath = `${this.sliderGalleryLocation}/${item.name}`;
      formData.append(`files[${index}]`, fullPath);
    });

    axios.post('/campaign/update-campaign-slider', formData, {
      headers: { 
        'X-CSRF-TOKEN': this.token,
        'Content-Type': 'multipart/form-data'
      }
    })
    .then(response => {
      this.$notify({ text: 'Media order updated successfully', type: 'success' });
    })
    .catch(error => {
      console.error('Failed to update media order:', error);
      this.$notify({ text: 'Failed to update media order', type: 'error' });
    });
  },
  extractYoutubeID(url) {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length == 11) ? match[2] : null;
  },
  addYoutubeVideo() {
    if (!this.youtubeUrl) {
      this.$notify({ text: 'Please enter a YouTube URL', type: 'error' });
      return;
    }

    const videoId = this.extractYoutubeID(this.youtubeUrl);
    if (!videoId) {
      this.$notify({ text: 'Invalid YouTube URL', type: 'error' });
      return;
    }

    const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/0.jpg`;
    const newMedia = {
      name: `youtube_${videoId}`,
      type: 'youtube',
      url: `https://www.youtube.com/watch?v=${videoId}`,
      thumbnailUrl: thumbnailUrl,
      videoUrl: `https://www.youtube.com/embed/${videoId}`
    };

    this.updateCampaignSlider(newMedia);
    this.youtubeUrl = '';
  },
  updateCampaignSlider(newMedia) {
    const formData = new FormData();
    formData.append('campaign_id', this.campaign.id);
    formData.append('type', newMedia.type);
    formData.append('url', newMedia.url);

    axios.post('/campaign/update-campaign-slider', formData, {
      headers: { 
        'X-CSRF-TOKEN': this.token,
        'Content-Type': 'multipart/form-data'
      }
    })
    .then(response => {
      console.log('Server response:', response.data);
      if (response.data && response.data.sliderImages) {
        this.sliderGalleryMediaComputed = response.data.sliderImages.map(item => ({
          name: item.url.split('/').pop(),
          type: item.type,
          url: item.url,
          thumbnailUrl: item.thumbnailUrl,
          videoUrl: item.videoUrl
        }));
        this.$notify({ text: 'YouTube video added successfully', type: 'success' });
        this.initializeSliderGallery(); // Refresh the gallery
      } else {
        throw new Error('Invalid response from server');
      }
    })
    .catch(error => {
      console.error('Failed to add YouTube video:', error);
      this.$notify({ text: `Failed to add YouTube video: ${error.response?.data?.error || error.message}`, type: 'error' });
    });
  },
  toggleTooltip(tooltipKey, show) {
    this.tooltip[tooltipKey] = show;
  },
  isTooltipVisible(fileName) {
    return this.hoveredThumbnail === fileName;
  },
  deleteMedia(index) {
    if (confirm('Are you sure you want to delete this media item?')) {
      const mediaItem = this.sliderGalleryMediaComputed[index];
      axios.post('/campaign/delete-campaign-slider-image', {
        campaign_id: this.campaign.id,
        imageName: mediaItem.name,
      }, {
        headers: {
          'X-CSRF-TOKEN': this.token,
        },
      })
      .then(response => {
        this.sliderGalleryMediaComputed.splice(index, 1);
        this.$notify({ text: 'Media item deleted successfully', type: 'success' });
      })
      .catch(error => {
        console.error('Failed to delete media item:', error);
        this.$notify({ text: 'Failed to delete media item', type: 'error' });
      });
    }
  },
  addMediaHandler(addedMedia, allMedia) {
    if (!addedMedia) {
      console.error('Invalid media object:', addedMedia);
      this.$notify({ text: 'Invalid media object', type: 'error' });
      return;
    }

    const file = addedMedia.file || addedMedia;
    const isVideo = file.type.startsWith('video/');
    
    const formData = new FormData();
    formData.append('image', file);
    formData.append('campaign_id', this.campaign.id);
    formData.append('type', isVideo ? 'video' : 'image');

    console.log('Sending formData:', formData);

    axios.post('/campaign/update-campaign-slider', formData, {
      headers: { 
        'X-CSRF-TOKEN': this.token,
        'Content-Type': 'multipart/form-data'
      }
    })
    .then(response => {
      console.log('Server response:', response.data);
      if (response.data && (response.data.url || response.data.sliderImages)) {
        if (response.data.url) {
          const newMedia = {
            name: file.name || 'Unnamed File',
            type: isVideo ? 'video' : 'image',
            url: response.data.url,
            thumbnailUrl: isVideo ? '' : response.data.url,
            videoUrl: isVideo ? response.data.url : ''
          };
          this.sliderGalleryMediaComputed.push(newMedia);
        } else if (response.data.sliderImages) {
          this.sliderGalleryMediaComputed = response.data.sliderImages;
        }
        this.$notify({ text: `Slider ${isVideo ? 'Video' : 'Image'} added successfully`, type: 'success' });
        this.initializeSliderGallery(); // Refresh the gallery
        
        // Remove the added media from the Uploader component
        if (this.$refs.uploader && typeof this.$refs.uploader.removeFile === 'function') {
          this.$refs.uploader.removeFile(file);
        }
      } else {
        throw new Error('Invalid response from server');
      }
    })
    .catch(error => {
      console.error('Failed to add media:', error);
      this.$notify({ text: `Failed to add ${isVideo ? 'video' : 'image'}: ${error.message}`, type: 'error' });
    });
  },
  removeMediaHandler(removedImage, removedMedia) {
    const campaignId = this.campaign.id;
    axios.post(`/campaign/delete-campaign-slider-image`, {
      campaign_id: campaignId,
      imageName: removedImage.name, // Adjust according to what the backend expects
    }, {
      headers: {
        'X-CSRF-TOKEN': this.token,
      },
    })
    .then(response => {
      console.log('Image deletion successful:', response.data);
      this.$notify({ text: 'Slider Image deleted successfully', type: 'success' });
      // Optionally, refresh your campaign or image list here
    })
    .catch(error => {
      console.error('Image deletion failed:', error);
      this.$notify({ text: 'Error deleting Slider Image', type: 'error' });
    });
  },
  onUploadSuccess(response) {
    // Handle successful upload, response from your server
    console.log('Upload Success', response);
  },
  onUploadError(error) {
    // Handle error
    console.error('Upload Error', error);
  },
  toggleView() {
    if (this.form.view_type === 'Legacy') {
      this.form.view_type = 'Refreshed';
    } else {
      this.form.view_type = 'Legacy';
    }
  },
  onClick() {
    if (this.open) {
    this.open()
    }
  },
  initializeSliderGallery() {
    let mediaItems = [];
    if (this.campaign.slider_images) {
      try {
        mediaItems = JSON.parse(this.campaign.slider_images);
      } catch (error) {
        console.error('Error parsing slider_images:', error);
      }
    }

    if (!Array.isArray(mediaItems) || mediaItems.length === 0) {
      if (this.parentCampaign && this.parentCampaign.slider_images) {
        try {
          mediaItems = JSON.parse(this.parentCampaign.slider_images);
        } catch (error) {
          console.error('Error parsing parent campaign slider_images:', error);
        }
      }
    }

    if (Array.isArray(mediaItems) && mediaItems.length > 0) {
      const firstItem = mediaItems[0];
      if (firstItem && firstItem.url) {
        const url = new URL(firstItem.url);
        this.sliderGalleryLocation = url.origin + url.pathname.substring(0, url.pathname.lastIndexOf('/'));
      }
      this.sliderGalleryMedia = mediaItems.map(item => ({
        name: item.url.split('/').pop(),
        type: item.type,
        url: item.url,
        thumbnailUrl: item.thumbnailUrl || item.url,
        videoUrl: item.videoUrl || ''
      }));
    } else {
      this.sliderGalleryLocation = '';
      this.sliderGalleryMedia = [];
    }
    console.log('Initialized slider gallery:', this.sliderGalleryMedia);
  },
  updateMediaOrder() {
    const formData = new FormData();
    formData.append('campaign_id', this.campaign.id);
    
    // Append the current order of files
    this.sliderGalleryMediaComputed.forEach((item, index) => {
      formData.append(`files[${index}]`, item.url);
    });

    axios.post('/campaign/update-campaign-slider', formData, {
      headers: { 
        'X-CSRF-TOKEN': this.token,
        'Content-Type': 'multipart/form-data'
      }
    })
    .then(response => {
      this.$notify({ text: 'Media order updated successfully', type: 'success' });
      // Refresh the gallery to ensure we're showing the latest data
      this.initializeSliderGallery();
    })
    .catch(error => {
      console.error('Failed to update media order:', error);
      this.$notify({ text: 'Failed to update media order', type: 'error' });
    });
  },
  getCampaign() {
    return axios.get('/campaign/' + this.campaign.id)
      .then(response => {
        console.log('Campaign data:', response.data);
        this.campaign = response.data.campaign
        this.cards = response.data.cards
        this.showStandard = response.data.showStandard
        this.form.campaignCards = response.data.campaignCards

        this.initializeSliderGallery()

        const heroImage = [
          '/img/side-out-gradient-bg-green@2x.jpg',
          '/img/side-out-gradient-bg-blue@2x.jpg',
          '/img/side-out-gradient-bg-pink@2x.jpg',
          '/img/side-out-gradient-bg@2x.jpg',
        ]
        if (!heroImage.includes(this.campaign.hero_image_path)) {
          this.uploadYourHero = 'yours'
          this.form.hero_image_text = null
        }
      })
      .catch(error => {
        console.error('Error fetching campaign:', error);
        this.$notify({ text: 'Failed to fetch campaign data', type: 'error' });
        throw error;
      });
  },
  sendingEvent(file, xhr, formData) {
    formData.append('_token', this.token);
    formData.append('campaign_id', this.campaign.id);
  },
  updateCampaign() {
    if (!this.form.name) {
    this.$notify({
      text: 'Campaign Name is required',
      type: 'error',
    })
    return
    }
    if (!this.form.goal) {
    this.$notify({
      text: 'Goal is required',
      type: 'error',
    })
    return
    }

    /*
    if (this.showStandard == false && !this.form.ownership) {
    this.$notify({
      text: "I'm Registering... is required",
      type: 'error',
    })
    return
    }
    */

    if (this.isPledgeDrive && !this.form.pledge_drive_end_date) {
    this.$notify({
      text: "Pledge Drive End Date is required",
      type: 'error',
    })
    return
    }

    if (this.isPledgeDrive && !this.form.pledge_drive_activity_type) {
    this.$notify({
      text: "Pledge Drive Activity Type is required",
      type: 'error',
    })
    return
    }

    this.saving = 'Updating...'

    axios.post('/update-campaign/' + this.campaign.id, this.form)
      .then(response => {

      console.log(response.data)
      if (response.data.type == 'success') {
        
        if (this.form.view_type !== 'Refreshed') {
          this.$refs.dropzone.processQueue();
          this.$refs.dropzone2.processQueue();
        }

        setTimeout(() => {
        this.getCampaign()
        this.$notify({text: 'Campaign has been updated.', type: 'success',})
        this.saving = 'Save'
        }, 2000)
      } else {
        this.$notify({text: response.data.message, type: 'error',})
      }
      })
  },
  sending(files, xhr, formData) {
    formData.append('_token', this.token);
    formData.append('campaign_id', this.campaign.id);
  },
  getImpressionCount(campaignCard) {
    let impressions = campaignCard.impressions
    if (impressions.length == 0) {
    return 0;
    }
    return  impressions[0].view_count
  }
  },

  computed: {
  sliderGalleryMediaComputed: {
    get() {
      return this.sliderGalleryMedia;
    },
    set(newValue) {
      this.sliderGalleryMedia = newValue;
      this.campaign.slider_images = JSON.stringify(newValue.map(item => ({
        type: item.type || 'image',
        url: item.url,
        thumbnailUrl: item.thumbnailUrl || item.url,
        videoUrl: item.videoUrl || ''
      })));
    }
  },
  campaignLabel() {
    const ownershipMapping = {
      school: 'School Name',
      club: 'Club Name',
      clubteam: 'Club Team Name',
      business: 'Business Name',
      tournament: 'Tournament Name',
      individual: 'Individual Name',
      unset: 'Organization Name',
    };

    // Default to 'Campaign Name' if ownership does not match any key
    return ownershipMapping[this.myCampaign.ownership] || 'Organization Name';
  },
  token() {
    return $('meta[name="csrf-token"]').attr('content')
  },
  isPledgeDrive() {
    return this.myCampaign.isPledgeDrive
  },
  isFoundation() {
    return this.myCampaign.isFoundation
  },
  dragOptions() {
    return {
      disabled: !this.isFoundation,
    };
  },
  }
})
</script>

<style>
  .dropzone {
    border: none!important;
  }
  .media-item {
    cursor: move;
    position: relative;
  }
  .thumbnail {
    width: 100%;
    height: auto;
    aspect-ratio: 1 / 1;
    object-fit: cover;
    border-radius: 8px;
    transition: transform 0.3s ease;
  }
  .media-item:hover .thumbnail {
    transform: scale(1.05);
  }
  .delete-btn {
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  .media-item:hover .delete-btn {
    opacity: 1;
  }
  .tooltip {
    pointer-events: none;
    z-index: 10;
  }
  .sortable-drag .delete-btn,
  .sortable-drag .media-type-icon {
    display: none;
  }
  .mu-image-container {
    display: none !important;
  }
  .media-type-icon {
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  .media-item:hover .media-type-icon {
    opacity: 1;
  }
  .uploader .mu-elements-wraper > .mu-plusbox-container {
    margin: 0 !important;
  }
  .uploader .mu-container {
    background: none !important;
    border: none !important;
  }
  @media (min-width: 641px) {
    .uploader, .draggable {
      max-width: 100%;
    }
    .uploader .mu-plusbox-container {
      width: 120px;
      height: 120px;
    }
    .media-item {
      width: 120px;
      height: 120px;
    }
    .draggable {
      gap: 10px;
    }
  }
  @media (max-width: 640px) {
    .delete-btn,
    .media-type-icon {
      opacity: 1;
    }
    .draggable {
      gap: 20px;
    }
    .uploader {
      display: flex;
      justify-content: center;
    }
  }
</style>
